/*
 * <about-collage> — the About hero's photo pile spreads into a flat layout as the reader scrolls.
 * Progress (0 → 1) is tied to scroll: 0 where the page loads, 1 after scrolling ~75% of the collage's width.
 * Each photo gets its own eased --t, offset by its --d so the top of the pile lifts first.
 * All motion is in CSS transforms (section-about.css); this only writes --t. Reduced motion: stays stacked.
 */
if (!customElements.get("about-collage")) {
  class AboutCollage extends HTMLElement {
    connectedCallback() {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

      this.photos = Array.from(this.querySelectorAll(".about-collage__photo")).map(el => ({
        el,
        delay: parseFloat(getComputedStyle(el).getPropertyValue("--d")) || 0,
        t: -1
      }));
      if (!this.photos.length) return;

      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      };
      this.onResize = () => {
        this.measure();
        this.update();
      };

      // Only listen to scroll while the collage is near the viewport.
      this.observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          window.addEventListener("scroll", this.onScroll, { passive: true });
          this.update();
        } else {
          window.removeEventListener("scroll", this.onScroll);
        }
      }, { rootMargin: "25% 0px" });
      this.observer.observe(this);
      window.addEventListener("resize", this.onResize, { passive: true });
      window.addEventListener("load", this.onResize, { once: true });

      this.measure();
      this.update();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      window.removeEventListener("scroll", this.onScroll);
      window.removeEventListener("resize", this.onResize);
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    measure() {
      const rect = this.getBoundingClientRect();
      const docTop = rect.top + window.scrollY;
      // Start once the collage has risen past 60% of the viewport — or immediately on scroll if it
      // already sits above that line at load — so progress is always 0 on arrival.
      const startLine = Math.min(window.innerHeight * 0.6, docTop);
      this.start = docTop - startLine;
      this.distance = Math.max(rect.width * 0.75, window.innerHeight * 0.45, 1);
    }

    update() {
      const progress = (window.scrollY - this.start) / this.distance;
      this.photos.forEach(photo => {
        const local = Math.min(Math.max((progress - photo.delay) / 0.76, 0), 1);
        const t = Math.round(easeInOutCubic(local) * 1000) / 1000;
        if (t !== photo.t) {
          photo.t = t;
          photo.el.style.setProperty("--t", t);
        }
      });
    }
  }

  // Scrubbed motion: gentle in-out so the pile eases off and settles rather than moving linearly.
  const easeInOutCubic = x => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);

  customElements.define("about-collage", AboutCollage);
}
